import React, {useState} from 'react';
import {Nav, Navbar} from 'react-bootstrap';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {MobileView} from 'react-device-detect';
import Badge from '@mui/material/Badge';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from 'app/store';
import {useAppSelector} from 'store/hooks';
import {
    toggleAuthModal,
    toggleCartDrawer,
    toggleMoreOptionsDrawer,
    toggleUpsellModal,
} from "app/store/slices/authSlice";
import { Routes } from 'app/shared/constants/constants';
import useGetPlatformInfo from 'hooks/useGetPlatformInfo';
import useIsTStore from 'hooks/useIsTStore';
import {ReactComponent as Cart} from "../../../assets/images/cart.svg";
import {ReactComponent as Bars} from "../../../assets/images/bars.svg";
import {ReactComponent as RewardsIcon} from "../../../assets/images/ebb_rewards.svg";
import {ReactComponent as MenuIcon} from "../../../assets/images/ebb_menu.svg";

enum FooterConstants {
    MENU = 'menu',
    REWARDS = 'rewards',
    CART = 'cart',
    MORE = 'more',
}

const MobileFooter: React.FC = () => {
    const {
        modalOpen,
        showFooter,
        hasSeenUpsellModal,
    } = useSelector((state: RootState) => state.auth);
    const isTStore = useIsTStore();
    const totalItemsInTheCart = useAppSelector((state) => (
        isTStore
            ? state.tOrders.totalCartItems
            : state.orders.totalItems
    ));
    const {user, isGuest, configs} = useSelector((state: RootState) => state.auth);
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [activeSection, setActiveSection] = useState(FooterConstants.MENU);
    const upsellFeatureEnabled = configs.env.REACT_APP_UPSELL_ENABLED === 'true';
    const { isShellApp } = useGetPlatformInfo();
    const hasCartItemsForUpsell = totalItemsInTheCart > 0;
    const shouldOpenUpsell = !hasSeenUpsellModal
        && upsellFeatureEnabled
        && hasCartItemsForUpsell;

    const redirectToHome = () => {
        // redirect to home on clicking rewards from mobile footer
        // for the signup, forgot password and reset password deeplinks
        const pages = ["signup", "forgotpassword", "signUp", "join", "forgotPassword", "passwordreset", "passwordReset"];
        if (pages.some((data) => window.location.href.includes(data))) {
            navigate("/");
        }
    };

    const handleModal = () => {
        if (user && !isGuest) {
            navigate("/rewards");
        } else {
            redirectToHome();
            dispatch(toggleAuthModal(!modalOpen));
        }
    };

    const openCart = () => {
        if (shouldOpenUpsell) {
            //show upsell modal
            dispatch(toggleUpsellModal(true));
            return;
        }
        dispatch(toggleCartDrawer(true));
    };

    const handleSelect = (key:FooterConstants) => {
        setActiveSection(key);
        if (key === FooterConstants.MORE) {
            dispatch(toggleMoreOptionsDrawer(true));
        }
    };
    // Hide MobileFooter in embed mobile giftcard transfer
    if (showFooter && location.pathname !== Routes.MOBILE_GIFTCARD_TRANSFER_ROUTE) {
        return (
            <MobileView>
                <Navbar
                    className="navBarBackground-footer pb-0 pt-0"
                    fixed="bottom"
                    expand="sm"
                    variant="light"
                >
                    <Nav className="d-flex flex-row flex-wrap flex-grow-1">
                        <Nav.Item className="d-flex flex-column footerItem" onClick={() => { handleSelect(FooterConstants.MENU); }}>
                            <Nav.Link className="footer-link" as={Link} to="/category">
                            <MenuIcon className={`footer-brand-icon footer-icons
                                ${activeSection === FooterConstants.MENU ? "footer-icons-color-nofade" : " footer-icons-color-fade"}`}
                            />
                            <p className={`menuFontFooter ${activeSection === FooterConstants.MENU ? "footer-icons-color-nofade" : " footer-icons-color-fade"}`}>
                                MENU
                            </p>
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item className="d-flex flex-column footerItem" onClick={() => { handleSelect(FooterConstants.REWARDS); }}>
                            <Nav.Link className="footer-link" onClick={handleModal}>
                            <RewardsIcon className={`footer-brand-icon footer-icons
                                ${activeSection === FooterConstants.REWARDS ? "footer-icons-color-nofade" : "footer-icons-color-fade"}`}
                            />
                            <p className={`menuFontFooter ${activeSection === FooterConstants.REWARDS ? "footer-icons-color-nofade" : " footer-icons-color-fade"}`}>
                                REWARDS
                            </p>
                            </Nav.Link>
                        </Nav.Item>
                        {!isShellApp && (
                            <Nav.Item className="d-flex flex-column footerItem" onClick={() => { handleSelect(FooterConstants.CART); }}>
                                <Nav.Link className="footer-link" onClick={openCart}>
                                <Badge badgeContent={totalItemsInTheCart} color="error" className="mobileFooterCart">
                                    <Cart className={`footer-icons ${activeSection === FooterConstants.CART ? "footer-icons-color-nofade" : "footer-icons-color-fade"}`}/>
                                </Badge>
                                <p className={`menuFontFooter ${activeSection === FooterConstants.CART ? "footer-icons-color-nofade" : " footer-icons-color-fade"}`}>
                                   CART
                                </p>
                                </Nav.Link>
                            </Nav.Item>
                        )}
                        <Nav.Item className="d-flex flex-column footerItem" onClick={() => { handleSelect(FooterConstants.MORE); }}>
                            <Nav.Link className="footer-link">
                            <Bars className={`footer-icons
                                ${activeSection === FooterConstants.MORE ? "footer-icons-color-nofade" : "footer-icons-color-fade"}`}
                            />
                            <p className={`menuFontFooter ${activeSection === FooterConstants.MORE ? "footer-icons-color-nofade" : " footer-icons-color-fade"}`}>
                                MORE
                            </p>
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                </Navbar>
            </MobileView>
        );
    }
    return null;
};

export default MobileFooter;
